import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { notify } from "@/components/ui/notify";
import {
	formatRate,
	formatRateDay,
	isWebUrl,
	todayUtc,
} from "@/features/catalog/lib/gst-rates";
import {
	fieldErrorsOf,
	type GstRate,
	recordGstSignOff,
} from "@/features/catalog/services/gst-rates-api.service";
import { apiErrorMessage } from "@/lib/api-error";

const TITLE_ID = "gst-sign-off-dialog-title";

/**
 * Record the sign-off a seeded rate never had (#514, R9). Once only; the rate
 * and its dates do not change.
 */
export function GstSignOffDialog({
	rate,
	onClose,
	onChanged,
}: {
	rate: GstRate;
	onClose: () => void;
	onChanged: () => void;
}) {
	const [signedOffBy, setSignedOffBy] = useState("");
	const [signedOffOn, setSignedOffOn] = useState(todayUtc());
	const [serial, setSerial] = useState(rate.notificationSerial ?? "");
	const [evidenceUrl, setEvidenceUrl] = useState("");

	const save = useMutation({
		mutationFn: () =>
			recordGstSignOff(rate.id, {
				signedOffBy: signedOffBy.trim(),
				signedOffOn,
				notificationSerial: serial.trim(),
				evidenceUrl: evidenceUrl.trim() || null,
			}),
		onSuccess: () => {
			notify.done(`Sign-off recorded for ${rate.hsnPrefix}`);
			onChanged();
			onClose();
		},
		onError: (error) => {
			// Someone recorded it first: show theirs.
			if (
				(error as { response?: { status?: number } })?.response?.status === 409
			) {
				onChanged();
			}
		},
	});

	const evidenceOk = evidenceUrl.trim() === "" || isWebUrl(evidenceUrl.trim());
	const canSave =
		signedOffBy.trim() !== "" &&
		serial.trim() !== "" &&
		/^\d{4}-\d{2}-\d{2}$/.test(signedOffOn) &&
		evidenceOk &&
		!save.isPending;
	const fields = save.isError ? fieldErrorsOf(save.error) : {};

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				if (!save.isPending) onClose();
			}}
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					Record sign-off for {rate.hsnPrefix}
				</h2>
				<p className="text-sm text-muted-foreground">
					{formatRate(rate.rateBp)} from {formatRateDay(rate.effectiveFrom)}.
					This records who approved the rate; it does not change it, and it can
					be recorded once.
				</p>
			</div>
			<div className="grid gap-1.5">
				<Label htmlFor="signoff-by">Signed off by</Label>
				<Input
					id="signoff-by"
					value={signedOffBy}
					onChange={(event) => setSignedOffBy(event.target.value)}
				/>
				{fields.signedOffBy ? (
					<p className="text-xs text-red-700">{fields.signedOffBy}</p>
				) : null}
			</div>
			<div className="grid gap-1.5">
				<Label htmlFor="signoff-on">Signed off on</Label>
				<Input
					id="signoff-on"
					type="date"
					value={signedOffOn}
					onChange={(event) => setSignedOffOn(event.target.value)}
				/>
			</div>
			<div className="grid gap-1.5">
				<Label htmlFor="signoff-serial">S. No.</Label>
				<Input
					id="signoff-serial"
					value={serial}
					onChange={(event) => setSerial(event.target.value)}
					placeholder="Schedule II S. No. 75"
				/>
			</div>
			<div className="grid gap-1.5">
				<Label htmlFor="signoff-evidence">Evidence link (optional)</Label>
				<Input
					id="signoff-evidence"
					value={evidenceUrl}
					onChange={(event) => setEvidenceUrl(event.target.value)}
					placeholder="https://"
				/>
				{evidenceOk ? null : (
					<p className="text-xs text-red-700">
						A link starts with https:// or http://.
					</p>
				)}
			</div>
			{save.isError && Object.keys(fields).length === 0 ? (
				<p role="alert" className="text-sm text-red-700">
					{apiErrorMessage(save.error, "Could not record the sign-off.")}
				</p>
			) : null}
			<div className="flex justify-end gap-2 border-t pt-3">
				<Button variant="ghost" onClick={onClose} disabled={save.isPending}>
					Cancel
				</Button>
				<Button onClick={() => save.mutate()} disabled={!canSave}>
					{save.isPending ? "Recording…" : "Record sign-off"}
				</Button>
			</div>
		</Dialog>
	);
}
